package com.bitterinfant.matthew

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.bitterinfant.shared.*
import com.bitterinfant.webui.DeepLink
import com.bitterinfant.webui.ImageBanner
import com.bitterinfant.webui.LockedLink
import com.bitterinfant.webui.PageShell
import com.bitterinfant.webui.Site
import com.bitterinfant.webui.Tabs
import com.bitterinfant.webui.Theme
import com.bitterinfant.webui.YouTubeEmbed
import com.bitterinfant.webui.AudioPlayer
import com.bitterinfant.webui.BannerImage
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable

fun main() {
    renderComposable(rootElementId = "root") {
        App()
    }
}

private val projects = listOf(
    Project("Demo Reel", "Editor / Composer", "2026", "A cut of recent editing and scoring work."),
)

private const val AUDIO_BASE = "https://audio.matthewthies.bitterinfantproductions.com"

/**
 * Renders one R2-hosted audio track. Pass [note] for an always-visible subtitle line, or
 * [tooltip] for hover-only text (native `title` attribute) that doesn't take up page space —
 * see the Jed's Dead tracks below for the tooltip form. [anchor] overrides the slugified title
 * in deep links (`?tab=samples&play=yusif`) — see [DeepLink].
 */
@Composable
private fun Track(title: String, filename: String, mimeType: String, note: String? = null, tooltip: String? = null, anchor: String? = null) {
    AudioPlayer(
        MediaItem(
            title = title,
            sources = listOf(MediaSource("$AUDIO_BASE/$filename", mimeType, MediaKind.AUDIO)),
        ),
        note = note,
        tooltip = tooltip,
        anchor = anchor,
    )
}


private val tabLabels = listOf("Film", "Samples", "Jed's Dead")

/** Banner photo per tab, indexed to match [tabLabels]. */
private val bannerImages = listOf(
    // Eyes sit ~17% down the photo, so "cover" (vertically centered) cut them off. At 130% height,
    // pinned near the top right, the cap/eyes/trumpet all show; the face is ~75% across.
    BannerImage("/media/main_banner.jpg", heightPercent = 130, verticalPercent = 10, focusX = 75),  // Film
    // Height-scaled and pinned right so the face sits right of center, balancing the links.
    BannerImage("/media/headshot.jpg", heightPercent = 160, verticalPercent = 35, focusX = 52),  // Samples
    // Small portrait photo: "cover" upscaled it ~4x and cropped the head. At 115% height nearly
    // the whole head shows.
    BannerImage("/media/morerecent.jpg", heightPercent = 115, verticalPercent = 40),  // Jed's Dead
)

@Composable
fun App() {
    val initialTab = remember { DeepLink.tabIndex(tabLabels) }
    var activeTab by remember { mutableStateOf(initialTab) }
    PageShell(
        current = Site.MATTHEW,
        title = "Matthew Thies",
        tagline = "Coder, Producer, Composer",
        // No header bar: the title/tagline live inside the banner instead, so the photo and
        // its gradient run all the way to the top of the page with no dividing line.
        showHeader = false,
    ) {
        // marginBottom(0) drops the extra gap we'd otherwise leave before the tabs.
        Section(attrs = { style { marginBottom(0.px) } }) {
            val banner = bannerImages[activeTab]
            ImageBanner(
                image = banner,
                imageAlt = "",
                flushTop = true,
            ) {
                H1(attrs = { style { margin(0.px); fontSize(1.6.cssRem) } }) { Text("Matthew Thies") }
                P(attrs = { style { margin(0.px); color(Color(Theme.textMuted)) } }) { Text("Coder, Producer, Composer") }
                // Equal top/bottom margins center the links in the gap between the tagline
                // and the tab strip (which overlaps the banner's bottom 56px); the banner is
                // content-sized, so these margins alone set that spacing.
                P(attrs = { style { marginTop(3.cssRem); marginBottom(3.cssRem) } }) {
                    Text("Code on ")
                    A(href = "https://github.com/matthewthies73", attrs = { attr("target", "_blank") }) { Text("GitHub") }
                    Text(",")
                    Br()
                    Text("Career on ")
                    A(href = "https://www.linkedin.com/in/matthew-thies-vegadev/", attrs = { attr("target", "_blank") }) { Text("LinkedIn") }
                    Text(",")
                    Br()
                    Text("Full credits on ")
                    A(href = "https://www.imdb.com/name/nm1907107/", attrs = { attr("target", "_blank") }) { Text("IMDb") }
                    Text(".")
                }
            }
        }
        // Pulls the tab strip up by the same 56px the banner grew by, so it overlaps the
        // bottom of the photo instead of sitting in the black space below it; position:
        // relative puts it in the same paint layer as the (position: relative) banner, so
        // it draws on top rather than getting covered by it.
        Div(attrs = { style { position(Position.Relative); marginTop((-56).px) } }) {
            Tabs(labels = tabLabels, initial = initialTab, onSelect = { activeTab = it }) { active ->
                when (active) {
                    0 -> Section {
                        YouTubeEmbed("cSUM_nPrHEA", anchor = "suburban-warfare", caption = "Suburban Warfare (short, 2005-ish); co-directed with Edward Romero; Music by myself featuring Johnny Hawthorn", tooltip = "When Edward Romero and I first met we collaborated on a short film we shot fast and didn't have much money for post-production but the dude that plays the main AI character became our roommate and was eventually cast again in Butterfly.  Rock on Garrett")
                        YouTubeEmbed(
                            "i4QHP7nn7EY",
                            anchor = "butterfly",
                            caption = "Butterfly (2012) Producer, Editor, Co-Composer",
                            captionUrl = "https://www.amazon.com/gp/video/detail/B0GM28QYNT?ref_=atv_dp_share_cu_r",
                            tooltip = "Producer, Editor, Composer and Production Stuff too; I was very busy on this film",
                        )
                        YouTubeEmbed("DufBEGj6fZM", anchor = "sibling-rivalry", caption = "Sibling Rivalry (2004-ish?) Sound Editor, Composer", tooltip = "This was a production by Margo Romero and Edward Romero.")
                    }
                    1 -> Section {
                        Track("Cosmic Calamity", "CosmicCalamity.mp3", "audio/mpeg", tooltip = "Main Theme for Pilot Cooking Show")
                        Track("Changeling", "Changeling.mp3", "audio/mpeg", tooltip = "Main Theme for 'Changeling' movie (unfinished/unproduced?)")
                        Track("Yusif", "yusif.mp3", "audio/mpeg", tooltip = "From Butterfly; used during a particularly gruesome scene; I included the entire track here.")
                        Track("Fabulous Family Life", "FabulousFamilyLife.mp3", "audio/mpeg", tooltip = "From Butterfly; Edward Romero who directed this film used temporary audio tracks during mixdown.  This is a mimic of the temp track that was used.  I cannot remember where the original is or what it's called.")
                        Track("Lake Wobegon", "LakeWobegonMaster.mp3", "audio/mpeg", tooltip = "A theme inspired by Lake Wobegon; and earlier effort of mine.")
                        Track("Best Friend", "bestfriend.mp3", "audio/mpeg", tooltip = "Song about FND/FMD being undiagnosed for 30+ years")
                        LockedLink("Best Friend Video", endpoint = "/api/best-friend")
                        Track("Phobic Main Theme", "phobic.mp3", "audio/mpeg", tooltip = "From Phobic; Margo Romero directed; asked that each character have their own theme and instrument.  This is all of that played at once.")
                        Track("Deliberation", "Deliberation.mp3", "audio/mpeg", tooltip = "From Butterfly")
                        Track("Power of Love by Huey Lewis; greenfield effort", "poweroflove-partialvoc.mp3", "audio/mpeg", anchor = "power-of-love", tooltip = "This was recorded for a wedding party entrance song where we had the wedding party sing and record all of the vocals.  This shows the instrumentation and then at the end I include the wedding party stuff which was a slightly different arrangement than the original.  I literally played every instrument via sample or physical.  I did not sample anything from the original.")
                        Track("Closed Eyes (Short Film)", "closedeyes.mp3", "audio/mpeg", tooltip = "From a short film by Dawn and Marie but for the life of me I cannot remember the title.  It's probably in IMDb")
                        Track("Frat Brat", "FratBratMaster.mp3", "audio/mpeg", tooltip = "From Issues 101 (credited as Rudy Matthews)")
                        Track("Party Song", "PartySongMaster.mp3", "audio/mpeg", tooltip = "From Sibling Rivalry")
                        Track("1st Orchestration Effort (2001)", "firstorchestration.mp3", "audio/mpeg", tooltip = "This is my 1st orchestration attempt with a Roland 5150 ? module with the added orchestral expansion pack.  I used a hard-disk recorder and used every single track available for each section.")
                    }
                    else -> Section {
                        P {
                            A(href = "https://open.spotify.com/artist/51KBemHN0xhXcuR6i7Sn3f", attrs = { attr("target", "_blank") }) { Text("Jed's Dead on Spotify") }
                        }
                        Track("Losing Side", "losingside.mp3", "audio/mpeg", tooltip = "Edward Romero and I co-wrote this (mostly the lyrics; he had the chord progress down) over a drink at a local Mexican restaurant and this is the first time I attempted to record my harp playing.  I just tried to make it work.")
                        Track("Party Wrecker", "partywrecker.mp3", "audio/mpeg", tooltip = "Edward Romero wrote this song on his way to rehearsal and I've only included it to show my piano skills; and harmony skills of course. I cover this on the accordion now.  Much fun!")
                        YouTubeEmbed("_dvpseT3AUY", anchor = "kalifornia", caption = "Kalifornia by Jed's Dead; edited by Matthew Thies")
                        YouTubeEmbed("oegC3NJF0so", anchor = "girl-from-texas", caption = "This was our first video for 'Girl From Texas' by Jed's Dead.")
                    }
                }
            }
        }
    }
}
